import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import {
  BiCardView,
  BiLayoutItem,
  kBiGridColumns,
  kBiMaxItemHeight,
} from '@euler/model/bi/dashboard';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import {
  biGridHeight,
  biItemRect,
  biSnapCells,
} from '../../functions/biGridGeometry';
import {
  biCardFits,
  biFitGridRows,
  biFitLines,
  biGridBottom,
  biMoveLevelItem,
  biResizeLevelItem,
  biSameGrid,
  biStoredRow,
} from '../../functions/biGridLayout';
import { ArrangeableItem, BiArrangeHandlers } from './ArrangeableItem';
import type { BiRenderCard } from './DashboardGrid';
import { useBiFitNeeds } from './useBiFitNeeds';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * The desktop grid with drag and resize, each row kept level (cards side by
 * side share a height). While a card is in hand the others move live to
 * where the drop would put them: a resized card takes its new size at once,
 * a dragged one follows the pointer over an outline of its landing spot. A
 * preset dashboard arrives here already converted to items, drawn exactly
 * as the preset draws it; the first drop saves those items as its custom
 * layout. A KPI group is drawn no shorter than its content, as on the plain
 * grid (`biFitGridRows`), and cannot be resized shorter than it.
 */
export const ArrangeableGrid = memo(
  ({
    items,
    revision,
    width,
    cards,
    renderCard,
    onArrange,
  }: {
    items: BiLayoutItem[];
    /** The dashboard's revision: a new one replaces the optimistic grid. */
    revision: number;
    width: number;
    cards: Map<string, BiCardView>;
    renderCard: BiRenderCard;
    /** Saves the new grid; false when refused, which restores the old one. */
    onArrange: (items: BiLayoutItem[]) => Promise<boolean>;
  }) => {
    const [pending, setPending] = useState<BiLayoutItem[] | null>(null);
    // A new copy of the dashboard replaces whatever was optimistic.
    useEffect(() => setPending(null), [revision]);
    const shown = pending ?? items;
    const { needs, report } = useBiFitNeeds(width, cards);
    // Where each card is drawn: a gesture starts from what the pointer sees,
    // and its target maps back to stored rows before the grid changes.
    const drawn = useMemo(() => biFitGridRows(shown, needs), [shown, needs]);
    const lines = useMemo(() => biFitLines(shown, needs), [shown, needs]);
    // The grid as the drop would leave it, while a card is dragged or
    // resized: the other cards make room live, and the outline marks where
    // the card itself lands.
    const [preview, setPreview] = useState<{
      cardId: string;
      /** A resize draws the card at its new size; a drag, under the pointer. */
      resizing: boolean;
      items: BiLayoutItem[];
    } | null>(null);
    // The cell the preview was last computed for, so a pointer moving
    // within one cell redraws nothing.
    const lastCell = useRef('');

    const handlers = useMemo<BiArrangeHandlers>(() => {
      const find = (cardId: string) => {
        const stored = shown.find(item => item.cardId === cardId);
        const at = drawn.find(item => item.cardId === cardId);
        return stored && at ? { stored, at } : null;
      };
      const moved = ({ at }: { at: BiLayoutItem }, dx: number, dy: number) => {
        const { columns, rows } = biSnapCells(dx, dy, width);
        const x = clamp(at.x + columns, 0, kBiGridColumns - at.w);
        const y = biStoredRow(Math.max(0, at.y + rows), lines);
        return {
          resizing: false,
          cell: `move:${x},${y}`,
          next: () => biMoveLevelItem(shown, at.cardId, x, y),
        };
      };
      const resized = (
        { stored, at }: { stored: BiLayoutItem; at: BiLayoutItem },
        dx: number,
        dy: number,
      ) => {
        const { columns, rows } = biSnapCells(dx, dy, width);
        const w = clamp(stored.w + columns, 1, kBiGridColumns - stored.x);
        // A width-only resize keeps the stored height (a row grown beside a
        // fitting card is not stored); a taller or shorter one starts from
        // the height drawn and never goes below the content it holds.
        const need = needs[stored.cardId] ?? 1;
        const h =
          rows === 0
            ? stored.h
            : clamp(at.h + rows, need, Math.max(kBiMaxItemHeight, need));
        return {
          resizing: true,
          cell: `size:${w},${h}`,
          next: () => biResizeLevelItem(shown, stored.cardId, w, h),
        };
      };
      const show = (
        cardId: string,
        target: {
          resizing: boolean;
          cell: string;
          next: () => BiLayoutItem[];
        },
      ) => {
        const cell = `${cardId}:${target.cell}`;
        if (cell === lastCell.current) return;
        lastCell.current = cell;
        setPreview({
          cardId,
          resizing: target.resizing,
          items: target.next(),
        });
      };
      const clear = () => {
        lastCell.current = '';
        setPreview(null);
      };
      const commit = (next: BiLayoutItem[]) => {
        clear();
        // One save at a time; a drop during one is ignored.
        if (pending || biSameGrid(next, shown)) return;
        setPending(next);
        void onArrange(next).then(saved => {
          if (!saved) setPending(null);
        });
      };
      return {
        onCancel: clear,
        onDragMove: (cardId, dx, dy) => {
          const item = find(cardId);
          if (item) show(cardId, moved(item, dx, dy));
        },
        onDrop: (cardId, dx, dy) => {
          const item = find(cardId);
          if (item) commit(moved(item, dx, dy).next());
          else clear();
        },
        onResizeMove: (cardId, dx, dy) => {
          const item = find(cardId);
          if (item) show(cardId, resized(item, dx, dy));
        },
        onResizeEnd: (cardId, dx, dy) => {
          const item = find(cardId);
          if (item) commit(resized(item, dx, dy).next());
          else clear();
        },
      };
    }, [shown, drawn, lines, needs, pending, width, onArrange]);

    const placed = useMemo(
      () => (preview ? biFitGridRows(preview.items, needs) : drawn),
      [preview, drawn, needs],
    );
    // A drag outlines where the card lands; a resize shows the card itself.
    const ghost =
      preview && !preview.resizing
        ? placed.find(item => item.cardId === preview.cardId) ?? null
        : null;
    const bottom = Math.max(biGridBottom(drawn), biGridBottom(placed));
    return (
      <View style={{ height: biGridHeight(bottom) }}>
        {drawn.map(item => {
          const card = cards.get(item.cardId);
          if (!card) return null;
          // A dragged card stays where it was and follows the pointer; a
          // resized one and every other card take the preview.
          const at =
            item.cardId === preview?.cardId && !preview.resizing
              ? item
              : placed.find(entry => entry.cardId === item.cardId) ?? item;
          return (
            <ArrangeableItem
              key={item.cardId}
              card={card}
              rect={biItemRect(at, width)}
              fits={biCardFits(card.viz)}
              onMeasure={report}
              renderCard={renderCard}
              handlers={handlers}
            />
          );
        })}
        {ghost ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              ...biItemRect(ghost, width),
              // Above the cards: a resize's outline lies over the card itself.
              zIndex: 20,
              borderRadius: 14,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: aiColors.primary,
              backgroundColor: tint(aiColors.primary, 0.06),
            }}
          />
        ) : null}
      </View>
    );
  },
);
